import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function DemoPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    employees: '',
    interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Essai Gratuit 14 Jours Paris - AgentIA Pro | Démo Agent IA Sans Engagement ROI 450%';

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    setMeta('description', 'Testez gratuitement nos agents IA à Paris pendant 14 jours. Agents IA conversationnels sans carte bancaire, sans engagement. Configuration agents IA en 5 minutes. Accès complet à toutes les fonctionnalités. Support dédié 24/7. ROI moyen 450%.');
    setMeta('keywords', 'essai gratuit agent IA Paris, démo gratuite IA, test agent conversationnel Paris, essai sans engagement agents IA, démo personnalisée IA Paris, trial gratuit intelligence artificielle');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/demo`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/demo`);
      document.head.appendChild(canonical);
    }

    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) { lastModified.setAttribute('content', new Date().toISOString().split('T')[0]); }
    else {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
      document.head.appendChild(lastModified);
    }

    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/demo` },
      { property: 'og:title', content: 'Essai Gratuit 14 Jours - AgentIA Pro | Démo Agent IA Paris' },
      { property: 'og:description', content: 'Testez gratuitement nos agents IA pendant 14 jours. Sans carte bancaire, sans engagement. ROI moyen 450%.' },
    ];
    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (tag) { tag.setAttribute('content', content); }
      else {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d6kv7sc6fojmejgair60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch (_) {
      // Continue even if form submission fails
    }

    setSubmitted(true);

    // Redirect immediately to onboarding
    setTimeout(() => {
      navigate('/onboarding');
    }, 1800);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-black"
    >
      {/* Structured Data for Offer */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": "Essai Gratuit AgentIA Pro Paris - 14 Jours Agents IA",
          "description": "Testez gratuitement tous nos agents IA intelligents à Paris pendant 14 jours sans carte bancaire ni engagement",
          "price": "0",
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock",
          "url": "https://agentia.pro/demo",
          "areaServed": {
            "@type": "City",
            "name": "Paris"
          },
          "seller": {
            "@type": "LocalBusiness",
            "name": "AgentIA Pro",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Paris",
              "addressCountry": "FR"
            }
          },
          "validFrom": "2024-01-01",
          "priceValidUntil": "2024-12-31",
          "itemOffered": {
            "@type": "Service",
            "name": "Agent IA Intelligent Conversationnel",
            "description": "Solution complète d'agents IA pour automatiser votre business"
          }
        })}
      </script>

      {/* FAQ Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "L'essai gratuit des agents IA est-il vraiment sans engagement ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Oui, absolument ! Vous pouvez tester toutes nos fonctionnalités d'agents IA pendant 14 jours sans carte bancaire. Aucun engagement pour les agents IA conversationnels, vous pouvez arrêter à tout moment."
              }
            },
            {
              "@type": "Question",
              "name": "Combien de temps faut-il pour configurer un agent IA ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "La configuration de base d'un agent IA prend moins de 5 minutes. Vous pouvez ensuite personnaliser votre agent IA selon vos besoins. Notre équipe est disponible pour vous accompagner dans la configuration de vos agents IA."
              }
            },
            {
              "@type": "Question",
              "name": "Puis-je changer d'agent IA pendant l'essai ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Oui ! Pendant votre période d'essai, vous pouvez tester tous nos agents IA intelligents et choisir l'agent IA qui correspond le mieux à vos besoins."
              }
            },
            {
              "@type": "Question",
              "name": "Que se passe-t-il après les 14 jours d'essai des agents IA ?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "À la fin de votre essai d'agents IA, vous pouvez choisir une formule payante pour continuer. Si vous ne souhaitez pas continuer avec les agents IA, votre compte sera simplement désactivé, sans frais."
              }
            }
          ]
        })}
      </script>

      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-black via-cyber-dark to-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyber-purple rounded-full blur-3xl opacity-20"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-block px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-6">
              <span className="text-xs font-body uppercase tracking-wider text-cyber-blue">
                <strong>Essai Gratuit 14 Jours Agents IA</strong> - Sans Carte Bancaire
              </span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight">
              <strong>Démarrez votre</strong><br />
              <span className="bg-gradient-to-r from-cyber-blue to-cyber-purple bg-clip-text text-transparent">
                Transformation IA
              </span>
            </h1>
            <p className="text-xl text-gray-400 font-body max-w-3xl mx-auto leading-relaxed">
              Testez <strong>gratuitement nos agents IA conversationnels</strong> pendant 14 jours. Sans carte bancaire, sans engagement. Activez votre premier <strong>agent IA intelligent</strong> en moins de <strong>5 minutes</strong>.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            {[
              {
                icon: 'ri-time-line',
                title: '14 jours gratuits',
                description: 'Testez toutes les fonctionnalités sans limite'
              },
              {
                icon: 'ri-bank-card-line',
                title: 'Sans carte bancaire',
                description: 'Aucune information de paiement requise'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Support dédié',
                description: 'Notre équipe vous accompagne 24/7'
              }
            ].map((benefit, index) => (
              <div
                key={index}
                className="p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-xl mb-4">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-400 font-body">
                  {benefit.description}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-cyber-dark">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl border border-white/20 p-8 lg:p-12">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-4">
                  Commencez votre essai gratuit
                </h2>
                <p className="text-gray-400 font-body">
                  Remplissez le formulaire et accédez <strong className="text-white">immédiatement</strong> à votre espace de configuration
                </p>
              </div>

              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-green-500/20 rounded-full mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-green-400"></i>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-white mb-3">
                    Compte activé instantanément !
                  </h3>
                  <p className="text-gray-400 font-body mb-4">
                    Redirection vers votre assistant de configuration…
                  </p>
                  <div className="flex items-center gap-3 text-cyber-blue">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-500/20 rounded-full shrink-0">
                      <i className="ri-flashlight-line text-green-400 text-lg"></i>
                    </div>
                    <p className="text-sm font-body">
                      <strong>Accès instantané</strong> — Dès la soumission, vous serez redirigé vers votre assistant de configuration. Aucune validation manuelle requise.
                    </p>
                  </div>
                </motion.div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-body font-semibold text-white mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 font-body text-sm focus:outline-none focus:border-cyber-blue transition-colors"
                        placeholder="Jean Dupont"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-body font-semibold text-white mb-2">
                        Email professionnel *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 font-body text-sm focus:outline-none focus:border-cyber-blue transition-colors"
                        placeholder="jean@entreprise.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-body font-semibold text-white mb-2">
                        Entreprise *
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 font-body text-sm focus:outline-none focus:border-cyber-blue transition-colors"
                        placeholder="Nom de votre entreprise"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-body font-semibold text-white mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 font-body text-sm focus:outline-none focus:border-cyber-blue transition-colors"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-body font-semibold text-white mb-2">
                        Taille de l'entreprise *
                      </label>
                      <select
                        name="employees"
                        value={formData.employees}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-body text-sm focus:outline-none focus:border-cyber-blue transition-colors cursor-pointer"
                      >
                        <option value="" className="bg-cyber-dark">Sélectionnez...</option>
                        <option value="1-10" className="bg-cyber-dark">1-10 employés</option>
                        <option value="11-50" className="bg-cyber-dark">11-50 employés</option>
                        <option value="51-200" className="bg-cyber-dark">51-200 employés</option>
                        <option value="201-500" className="bg-cyber-dark">201-500 employés</option>
                        <option value="500+" className="bg-cyber-dark">500+ employés</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-body font-semibold text-white mb-2">
                        Agent qui vous intéresse *
                      </label>
                      <select
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-body text-sm focus:outline-none focus:border-cyber-blue transition-colors cursor-pointer"
                      >
                        <option value="" className="bg-cyber-dark">Sélectionnez...</option>
                        <option value="vendeur" className="bg-cyber-dark">Agent Vendeur</option>
                        <option value="service-client" className="bg-cyber-dark">Agent Service Client</option>
                        <option value="prise-rdv" className="bg-cyber-dark">Agent Prise de RDV</option>
                        <option value="support-technique" className="bg-cyber-dark">Agent Support Technique</option>
                        <option value="marketing" className="bg-cyber-dark">Agent Marketing</option>
                        <option value="reseaux-sociaux" className="bg-cyber-dark">Agent Réseaux Sociaux</option>
                        <option value="rh-recrutement" className="bg-cyber-dark">Agent RH & Recrutement</option>
                        <option value="formation" className="bg-cyber-dark">Agent Formation Client</option>
                        <option value="analyse" className="bg-cyber-dark">Agent Analyse Business</option>
                        <option value="standard" className="bg-cyber-dark">Standard Téléphonique IA</option>
                      </select>
                    </div>
                  </div>

                  {/* Instant access badge */}
                  <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center bg-green-500/20 rounded-full shrink-0">
                      <i className="ri-flashlight-line text-green-400 text-lg"></i>
                    </div>
                    <p className="text-sm text-green-300 font-body">
                      <strong>Accès instantané</strong> — Dès la soumission, vous serez redirigé vers votre assistant de configuration. Aucune validation manuelle requise.
                    </p>
                  </div>

                  <div className="pt-6 border-t border-white/10">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white font-body font-semibold rounded-xl hover:shadow-2xl hover:shadow-cyber-blue/50 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          <span>Activation en cours…</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-flashlight-line text-xl"></i>
                          <span>Démarrer mon essai gratuit — Accès immédiat</span>
                          <i className="ri-arrow-right-line text-xl"></i>
                        </span>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 font-body text-center mt-4">
                      En soumettant ce formulaire, vous acceptez nos conditions d'utilisation et notre politique de confidentialité
                    </p>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-4">
              Pourquoi nous faire confiance ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Sécurité garantie',
                description: 'Données chiffrées et conformité RGPD'
              },
              {
                icon: 'ri-team-line',
                title: '500+ entreprises',
                description: 'Nous font déjà confiance'
              },
              {
                icon: 'ri-star-line',
                title: '4.9/5 étoiles',
                description: 'Note moyenne de nos clients'
              },
              {
                icon: 'ri-rocket-line',
                title: 'Setup en 5 min',
                description: 'Démarrez immédiatement'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-cyber-blue to-cyber-purple rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-400 font-body">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-cyber-dark">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-white mb-4">
              Questions fréquentes
            </h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                question: 'L\'essai gratuit est-il vraiment sans engagement ?',
                answer: 'Oui, absolument ! Vous pouvez tester toutes nos fonctionnalités pendant 14 jours sans carte bancaire. Aucun engagement, vous pouvez arrêter à tout moment.'
              },
              {
                question: 'Combien de temps faut-il pour configurer un agent ?',
                answer: 'La configuration de base prend moins de 5 minutes. Vous pouvez ensuite personnaliser votre agent selon vos besoins. Notre équipe est disponible pour vous accompagner.'
              },
              {
                question: 'Puis-je changer d\'agent pendant l\'essai ?',
                answer: 'Oui ! Pendant votre période d\'essai, vous pouvez tester tous nos agents IA et choisir celui qui correspond le mieux à vos besoins.'
              },
              {
                question: 'Que se passe-t-il après les 14 jours ?',
                answer: 'À la fin de votre essai, vous pouvez choisir une formule payante pour continuer. Si vous ne souhaitez pas continuer, votre compte sera simplement désactivé, sans frais.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              >
                <h3 className="text-lg font-display font-bold text-white mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-400 font-body leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </motion.div>
  );
}
